import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { History, Search, User, Calendar, FileText } from 'lucide-react';
import moment from 'moment';
import 'moment/locale/es';

moment.locale('es');
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, filterAction]);

  const fetchLogs = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/audit?limit=200`, {
        withCredentials: true,
      });
      setLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = logs;

    // Filtrar por tipo de acción
    if (filterAction !== 'all') {
      filtered = filtered.filter(log => log.action === filterAction);
    }

    // Filtrar por búsqueda (usuario o detalles)
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  };

  const getActionLabel = (action) => {
    const actions = {
      create_surgery: 'Cirugía Creada',
      update_surgery: 'Cirugía Actualizada',
      delete_surgery: 'Cirugía Eliminada',
      create_user: 'Usuario Creado',
      delete_user: 'Usuario Eliminado',
    };
    return actions[action] || action;
  };

  const getActionColor = (action) => {
    if (action.includes('create')) return 'bg-green-100 text-green-800 border-green-200';
    if (action.includes('update')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (action.includes('delete')) return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getActionIcon = (action) => {
    if (action.includes('create')) return '➕';
    if (action.includes('update')) return '✏️';
    if (action.includes('delete')) return '🗑️';
    return '📝';
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-[#F3EBE1] rounded-xl"></div>
          <div className="h-32 bg-[#F3EBE1] rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <History className="text-[#2B4C3B]" size={28} />
          <h1 className="text-2xl sm:text-4xl font-bold text-[#1A1C18] tracking-tight">
            Historial de Auditoría
          </h1>
        </div>
        <p className="text-[#6A736B] text-sm">Registro completo de todas las acciones en el sistema</p>
      </div>

      {/* Filters */}
      <Card className="bg-[#FFFFFF] rounded-xl border border-[#E8E5DD] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6A736B]" size={20} />
                <Input
                  placeholder="Buscar por usuario o detalles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[#FDFBF7] border-[#E8E5DD]"
                />
              </div>
            </div>
            <div className="w-full md:w-64">
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="bg-[#FDFBF7] border-[#E8E5DD]">
                  <SelectValue placeholder="Filtrar por acción" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las acciones</SelectItem>
                  <SelectItem value="create_surgery">Cirugías Creadas</SelectItem>
                  <SelectItem value="update_surgery">Cirugías Actualizadas</SelectItem>
                  <SelectItem value="delete_surgery">Cirugías Eliminadas</SelectItem>
                  <SelectItem value="create_user">Usuarios Creados</SelectItem>
                  <SelectItem value="delete_user">Usuarios Eliminados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="mt-4 text-sm text-[#6A736B]">
            Mostrando {filteredLogs.length} de {logs.length} registros
          </div>
        </CardContent>
      </Card>

      {/* Audit Log List */}
      <div className="space-y-3">
        {filteredLogs.length === 0 ? (
          <Card className="bg-[#FFFFFF] rounded-xl border border-[#E8E5DD] p-12 text-center">
            <FileText className="mx-auto mb-4 text-[#6A736B]" size={48} />
            <p className="text-[#6A736B]">No se encontraron registros</p>
          </Card>
        ) : (
          filteredLogs.map((log, index) => (
            <Card
              key={index}
              className="bg-[#FFFFFF] rounded-xl border border-[#E8E5DD] shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] transition-all duration-200"
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  {/* Icon and Action Badge */}
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                      log.action.includes('create') ? 'bg-green-100' :
                      log.action.includes('update') ? 'bg-blue-100' :
                      log.action.includes('delete') ? 'bg-red-100' : 'bg-gray-100'
                    }`}>
                      {getActionIcon(log.action)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getActionColor(log.action)}`}>
                          {getActionLabel(log.action)}
                        </span>
                        <div className="flex items-center gap-2 text-sm text-[#6A736B]">
                          <User size={14} />
                          <span className="font-medium text-[#1A1C18]">{log.user_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#6A736B] whitespace-nowrap">
                        <Calendar size={14} />
                        <span>{moment(log.timestamp).format('DD/MM/YYYY HH:mm')}</span>
                      </div>
                    </div>

                    <p className="text-[#1A1C18] mb-2">{log.details}</p>

                    {log.changes && (typeof log.changes === 'string'
                      ? log.changes.trim().length > 0
                      : Object.keys(log.changes).length > 0) && (
                      <details className="mt-3" data-testid="audit-changes-details">
                        <summary className="text-sm text-[#2B4C3B] cursor-pointer hover:underline font-medium">
                          Ver cambios realizados
                        </summary>
                        <div className="mt-2 p-3 bg-[#FDFBF7] rounded-lg border border-[#E8E5DD]">
                          {typeof log.changes === 'string' ? (
                            // Legacy format (registros antiguos): string plano
                            <pre className="text-xs text-[#6A736B] whitespace-pre-wrap break-all font-mono">
                              {log.changes}
                            </pre>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm" data-testid="audit-changes-table">
                                <thead>
                                  <tr className="text-left text-[#6A736B] border-b border-[#E8E5DD]">
                                    <th className="pb-2 pr-4 font-medium">Campo</th>
                                    <th className="pb-2 pr-4 font-medium">Antes</th>
                                    <th className="pb-2 font-medium">Después</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(log.changes).map(([key, value]) => {
                                    if (key === 'updated_at' || key === 'updated_by') return null;
                                    // Nuevo formato: {label, from, to}
                                    if (value && typeof value === 'object' && ('from' in value || 'to' in value)) {
                                      return (
                                        <tr key={key} className="border-b border-[#E8E5DD]/50 last:border-0">
                                          <td className="py-2 pr-4 font-medium text-[#1A1C18]">{value.label || key}</td>
                                          <td className="py-2 pr-4 text-red-700 line-through decoration-red-300/60">{value.from || '—'}</td>
                                          <td className="py-2 text-emerald-700 font-semibold">{value.to || '—'}</td>
                                        </tr>
                                      );
                                    }
                                    // Formato intermedio (dict simple {campo: valor})
                                    const displayValue = typeof value === 'boolean'
                                      ? (value ? 'Sí' : 'No')
                                      : String(value);
                                    return (
                                      <tr key={key} className="border-b border-[#E8E5DD]/50 last:border-0">
                                        <td className="py-2 pr-4 font-medium text-[#1A1C18] capitalize">{key.replace(/_/g, ' ')}</td>
                                        <td className="py-2 pr-4 text-[#6A736B]">—</td>
                                        <td className="py-2 text-emerald-700 font-semibold">{displayValue}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </details>
                    )}

                    {log.surgery_id && (
                      <div className="mt-2 text-xs text-[#6A736B]">
                        ID Cirugía: {log.surgery_id}
                      </div>
                    )}
                  </div>

                  {/* Relative Time */}
                  <div className="flex-shrink-0 text-right">
                    <span className="text-xs text-[#6A736B]">
                      {moment(log.timestamp).fromNow()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
